import React, { useEffect, useRef, useState, useMemo, createContext, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Slider,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Paper,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  TextIncrease as TextIncreaseIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';

// ==================== Time Frame Options ====================
const TIME_FRAME_OPTIONS = [
  { value: 'today',        label: 'Today',       getRange: () => getTodayRange() },
  { value: 'this_week',   label: 'This Week',   getRange: () => getThisWeekRange() },
  { value: 'last_week',   label: 'Last Week',   getRange: () => getLastWeekRange() },
  { value: 'this_month',  label: 'This Month',  getRange: () => getThisMonthRange() },
  { value: 'last_month',  label: 'Last Month',  getRange: () => getLastMonthRange() },
  { value: 'this_quarter', label: 'This Quarter', getRange: () => getThisQuarterRange() },
  { value: 'last_quarter', label: 'Last Quarter', getRange: () => getLastQuarterRange() },
  { value: 'this_year',   label: 'This Year',   getRange: () => getThisYearRange() },
  { value: 'last_year',   label: 'Last Year',   getRange: () => getLastYearRange() },
  { value: 'last_7_days', label: 'Last 7 Days', getRange: () => getLastNDaysRange(7) },
  { value: 'last_30_days', label: 'Last 30 Days', getRange: () => getLastNDaysRange(30) },
  { value: 'last_90_days', label: 'Last 90 Days', getRange: () => getLastNDaysRange(90) },
  { value: 'all_time',    label: 'All Time',    getRange: () => null },
];

// Time range helper functions
function getTodayRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getThisWeekRange() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon...
  const diffToMon = (day === 0 ? -6 : 1 - day);
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMon, 0, 0, 0);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6, 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getLastWeekRange() {
  const now = new Date();
  const day = now.getDay();
  const diffToMon = (day === 0 ? -6 : 1 - day);
  const thisMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMon, 0, 0, 0);
  const start = new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - 7, 0, 0, 0);
  const end = new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - 1, 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getThisMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getLastMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getThisQuarterRange() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), quarter * 3, 1);
  const end = new Date(now.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getLastQuarterRange() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) - 1;
  const year = quarter < 0 ? now.getFullYear() - 1 : now.getFullYear();
  const adjustedQuarter = quarter < 0 ? 3 : quarter;
  const start = new Date(year, adjustedQuarter * 3, 1);
  const end = new Date(year, adjustedQuarter * 3 + 3, 0, 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getThisYearRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getLastYearRange() {
  const now = new Date();
  const start = new Date(now.getFullYear() - 1, 0, 1);
  const end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getLastNDaysRange(days: number) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  start.setHours(0, 0, 0, 0);
  return { start: start.toISOString(), end: end.toISOString() };
}

// ==================== Widget Color Palette ====================
// Premium executive palette — 12 distinct corporate colors.
// All combinations maintain WCAG AA contrast (≥ 4.5:1) for white text.
export const WIDGET_COLORS = [
  // 0  Deep Navy — primary financial metric
  { name: 'Deep Navy',       gradient: 'linear-gradient(135deg, #0C1F3F 0%, #1a365d 100%)', text: '#ffffff', header: '#0C1F3F', valueColor: '#0C1F3F' },
  // 1  Ocean Blue — volume / counts
  { name: 'Ocean Blue',      gradient: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)', text: '#ffffff', header: '#1565C0', valueColor: '#0d47a1' },
  // 2  Deep Teal — savings / efficiency
  { name: 'Deep Teal',       gradient: 'linear-gradient(135deg, #0F4C5C 0%, #0891B2 100%)', text: '#ffffff', header: '#0F4C5C', valueColor: '#0e7490' },
  // 3  Forest Green — approvals / on-time
  { name: 'Forest Green',    gradient: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)', text: '#ffffff', header: '#1B4332', valueColor: '#166534' },
  // 4  Amber Gold — budget / utilization
  { name: 'Amber Gold',      gradient: 'linear-gradient(135deg, #78350F 0%, #B45309 100%)', text: '#ffffff', header: '#78350F', valueColor: '#92400e' },
  // 5  Crimson — pending / alerts
  { name: 'Crimson',         gradient: 'linear-gradient(135deg, #7F1D1D 0%, #B91C1C 100%)', text: '#ffffff', header: '#7F1D1D', valueColor: '#991b1b' },
  // 6  Royal Indigo — trend charts
  { name: 'Royal Indigo',    gradient: 'linear-gradient(135deg, #1E1B4B 0%, #3730A3 100%)', text: '#ffffff', header: '#1E1B4B', valueColor: '#312e81' },
  // 7  Slate — secondary charts
  { name: 'Slate',           gradient: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)', text: '#ffffff', header: '#1E293B', valueColor: '#1e293b' },
  // 8  Dark Teal — supplier spend
  { name: 'Dark Teal',       gradient: 'linear-gradient(135deg, #134E4A 0%, #0F766E 100%)', text: '#ffffff', header: '#134E4A', valueColor: '#115e59' },
  // 9  Deep Purple — distribution charts
  { name: 'Deep Purple',     gradient: 'linear-gradient(135deg, #2E1065 0%, #6D28D9 100%)', text: '#ffffff', header: '#2E1065', valueColor: '#4c1d95' },
  // 10 Steel Blue — operations
  { name: 'Steel Blue',      gradient: 'linear-gradient(135deg, #1E3A5F 0%, #2C5282 100%)', text: '#ffffff', header: '#1E3A5F', valueColor: '#1a365d' },
  // 11 Burgundy — status breakdown
  { name: 'Burgundy',        gradient: 'linear-gradient(135deg, #4C0519 0%, #9F1239 100%)', text: '#ffffff', header: '#4C0519', valueColor: '#881337' },
];

// ==================== Dashboard Context ====================
interface DashboardContextValue {
  fontSize: number;
  timeFrame: string;
  timeRange: { start: string; end: string } | null;
  isFullscreen: boolean;
  getWidgetColor: (index: number) => typeof WIDGET_COLORS[0];
  onDrillDown: (widgetId: string, widgetName: string, drillDownValue?: string) => void;
}

const DashboardContext = createContext<DashboardContextValue>({
  fontSize: 1,
  timeFrame: 'this_month',
  timeRange: null,
  isFullscreen: false,
  getWidgetColor: (index) => WIDGET_COLORS[index % WIDGET_COLORS.length],
  onDrillDown: () => {},
});

export const useDashboardContext = () => useContext(DashboardContext);
import {
  useGetDashboardByIdQuery,
  useFetchDashboardDataMutation,
  useUpdateDashboardMutation,
  DashboardWidget,
  WidgetDataResult,
} from '../../../redux/wallboardApi';
import { useAppSelector } from '../../../redux/hooks';
import { DrillDownDrawer, DrillDownTarget } from '../viewer/DrillDownDrawer';
import { selectCurrentUser } from '../../../redux/reducers/authSlice';
import { WidgetRenderer } from '../widgets/WidgetRenderer';
import { WidgetGrid } from '../viewer/WidgetGrid';
import PageWithHeader from '../../common/PageWithHeader';

/**
 * Dashboard View Page
 * 
 * Renders a dashboard with all its widgets and handles auto-refresh.
 * Features:
 * - Font size slider for accessibility
 * - Time frame filtering
 * - Colorful widget themes
 */
export const DashboardViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Drill-down drawer state (defined AFTER timeRange below)
  const [drillDownTarget, setDrillDownTarget] = useState<DrillDownTarget | null>(null);

  // Font size control (scale factor: 0.7 to 1.5)
  const [fontSize, setFontSize] = useState<number>(1);
  
  // Time frame control
  const [timeFrame, setTimeFrame] = useState<string>('this_month');
  
  // Track if settings have been initialized from dashboard
  const settingsInitialized = useRef(false);
  
  // Calculate time range from selected time frame
  const timeRange = useMemo(() => {
    const option = TIME_FRAME_OPTIONS.find(o => o.value === timeFrame);
    return option?.getRange() || null;
  }, [timeFrame]);

  // Drill-down callback — must be defined AFTER timeRange
  const onDrillDown = useCallback((widgetId: string, widgetName: string, drillDownValue?: string) => {
    if (!id) return;
    setDrillDownTarget({
      dashboardId: id,
      widgetId,
      widgetName,
      drillDownValue,
      timeRange: timeRange ? { start: timeRange.start, end: timeRange.end } : undefined,
    });
  }, [id, timeRange]);
  
  // Widget color getter
  const getWidgetColor = (index: number) => WIDGET_COLORS[index % WIDGET_COLORS.length];
  
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch dashboard configuration
  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    error: dashboardError,
  } = useGetDashboardByIdQuery(id!, { skip: !id });

  // Mutation for fetching widget data
  const [
    fetchData,
    { data: widgetData, isLoading: isDataLoading },
  ] = useFetchDashboardDataMutation();
  
  // Mutation for updating dashboard settings
  const [updateDashboard] = useUpdateDashboardMutation();

  // Check if user can edit this dashboard
  const canEdit = dashboard && currentUser && (
    dashboard.createdBy === currentUser.id || 
    currentUser.roles?.includes('admin')
  );
  
  // Initialize settings from dashboard viewSettings
  useEffect(() => {
    if (dashboard?.viewSettings && !settingsInitialized.current) {
      if (dashboard.viewSettings.fontSize !== undefined) {
        setFontSize(dashboard.viewSettings.fontSize);
      }
      if (dashboard.viewSettings.defaultTimeFrame) {
        setTimeFrame(dashboard.viewSettings.defaultTimeFrame);
      }
      settingsInitialized.current = true;
    }
  }, [dashboard?.viewSettings]);
  
  // Debounced save function for view settings
  const saveViewSettings = useCallback((newFontSize: number, newTimeFrame: string) => {
    if (!dashboard?.id || !canEdit) return;
    
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce the save to avoid too many API calls
    saveTimeoutRef.current = setTimeout(() => {
      updateDashboard({
        id: dashboard.id,
        body: {
          viewSettings: {
            fontSize: newFontSize,
            defaultTimeFrame: newTimeFrame,
          },
        },
      });
    }, 1000); // Save after 1 second of no changes
  }, [dashboard?.id, canEdit, updateDashboard]);
  
  // Cleanup save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Initial data fetch with time range
  useEffect(() => {
    if (dashboard?.id) {
      fetchData({ 
        dashboardId: dashboard.id,
        timeRange: timeRange || undefined,
      });
    }
  }, [dashboard?.id, fetchData, timeRange]);

  // Update lastUpdated when data is fetched
  useEffect(() => {
    if (widgetData?.fetchedAt) {
      setLastUpdated(new Date(widgetData.fetchedAt));
    }
  }, [widgetData]);

  // Auto-refresh setup
  useEffect(() => {
    if (dashboard?.refreshInterval && dashboard.refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        fetchData({ 
          dashboardId: dashboard.id,
          timeRange: timeRange || undefined,
        });
      }, dashboard.refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [dashboard?.refreshInterval, dashboard?.id, fetchData, timeRange]);

  // Fullscreen handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Manual refresh
  const handleRefresh = () => {
    if (dashboard?.id) {
      fetchData({ 
        dashboardId: dashboard.id,
        timeRange: timeRange || undefined,
      });
    }
  };

  // Font size change handler
  const handleFontSizeChange = (_: Event, value: number | number[]) => {
    const newFontSize = value as number;
    setFontSize(newFontSize);
    saveViewSettings(newFontSize, timeFrame);
  };

  // Time frame change handler
  const handleTimeFrameChange = (event: SelectChangeEvent) => {
    const newTimeFrame = event.target.value;
    setTimeFrame(newTimeFrame);
    saveViewSettings(fontSize, newTimeFrame);
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Loading state
  if (isDashboardLoading) {
    return (
      <PageWithHeader>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageWithHeader>
    );
  }

  // Error state
  if (dashboardError || !dashboard) {
    return (
      <PageWithHeader>
        <Alert severity="error">
          Failed to load dashboard. It may not exist or you may not have access.
        </Alert>
      </PageWithHeader>
    );
  }

  const widgetDataMap = widgetData?.widgets || {};

  // Dashboard context value
  const dashboardContextValue: DashboardContextValue = {
    fontSize,
    timeFrame,
    timeRange,
    isFullscreen,
    getWidgetColor,
    onDrillDown,
  };

  return (
    <DashboardContext.Provider value={dashboardContextValue}>
      <Box
        ref={containerRef}
        sx={{
          height: isFullscreen ? '100vh' : 'auto',
          minHeight: isFullscreen ? '100vh' : 'calc(100vh - 64px)',
          bgcolor: isFullscreen ? 'background.default' : 'transparent',
          p: isFullscreen ? 2 : 0,
          fontSize: `${fontSize}rem`,
        }}
      >
        {!isFullscreen && (
          <PageWithHeader>
            <Box sx={{ p: 2 }}>
              <DashboardHeader
                dashboard={dashboard}
                canEdit={canEdit || false}
                lastUpdated={lastUpdated}
                isRefreshing={isDataLoading}
                isFullscreen={isFullscreen}
                menuAnchor={menuAnchor}
                onBack={() => navigate('/wallboard')}
                onRefresh={handleRefresh}
                onToggleFullscreen={toggleFullscreen}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onEdit={() => navigate(`/wallboard/edit/${dashboard.id}`)}
              />
              
              {/* Controls Toolbar */}
              <DashboardControls
                fontSize={fontSize}
                timeFrame={timeFrame}
                onFontSizeChange={handleFontSizeChange}
                onTimeFrameChange={handleTimeFrameChange}
              />
              
              <DashboardContent
                dashboard={dashboard}
                widgetData={widgetDataMap}
                isLoading={isDataLoading}
              />
            </Box>
          </PageWithHeader>
        )}

        {isFullscreen && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <DashboardHeader
              dashboard={dashboard}
              canEdit={canEdit || false}
              lastUpdated={lastUpdated}
              isRefreshing={isDataLoading}
              isFullscreen={isFullscreen}
              menuAnchor={menuAnchor}
              onBack={() => setIsFullscreen(false)}
              onRefresh={handleRefresh}
              onToggleFullscreen={toggleFullscreen}
              onMenuOpen={handleMenuOpen}
              onMenuClose={handleMenuClose}
              onEdit={() => navigate(`/wallboard/edit/${dashboard.id}`)}
            />
            
            {/* Controls Toolbar */}
            <DashboardControls
              fontSize={fontSize}
              timeFrame={timeFrame}
              onFontSizeChange={handleFontSizeChange}
              onTimeFrameChange={handleTimeFrameChange}
            />
            
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              <DashboardContent
                dashboard={dashboard}
                widgetData={widgetDataMap}
                isLoading={isDataLoading}
              />
            </Box>
          </Box>
        )}
      </Box>
      <DrillDownDrawer
        target={drillDownTarget}
        onClose={() => setDrillDownTarget(null)}
      />
    </DashboardContext.Provider>
  );
};

/**
 * Dashboard Header Component
 */
interface DashboardHeaderProps {
  dashboard: any;
  canEdit: boolean;
  lastUpdated: Date | null;
  isRefreshing: boolean;
  isFullscreen: boolean;
  menuAnchor: HTMLElement | null;
  onBack: () => void;
  onRefresh: () => void;
  onToggleFullscreen: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onMenuClose: () => void;
  onEdit: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  dashboard,
  canEdit,
  lastUpdated,
  isRefreshing,
  isFullscreen,
  menuAnchor,
  onBack,
  onRefresh,
  onToggleFullscreen,
  onMenuOpen,
  onMenuClose,
  onEdit,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">{dashboard.name}</Typography>
        {!dashboard.isPublished && (
          <Chip label="Draft" size="small" color="warning" />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {lastUpdated && (
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        )}

        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} disabled={isRefreshing}>
            {isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
          <IconButton onClick={onToggleFullscreen}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>

        <IconButton onClick={onMenuOpen}>
          <MoreIcon />
        </IconButton>

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={onMenuClose}>
          {canEdit && (
            <MenuItem onClick={() => { onMenuClose(); onEdit(); }}>
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              Edit Dashboard
            </MenuItem>
          )}
          <MenuItem onClick={() => { onMenuClose(); /* TODO: Share */ }}>
            <ShareIcon sx={{ mr: 1 }} fontSize="small" />
            Share
          </MenuItem>
          {canEdit && <Divider />}
          {canEdit && (
            <MenuItem onClick={() => { onMenuClose(); onEdit(); }}>
              <SettingsIcon sx={{ mr: 1 }} fontSize="small" />
              Settings
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );
};

/**
 * Dashboard Controls Component
 * 
 * Toolbar with font size slider and time frame selector.
 */
interface DashboardControlsProps {
  fontSize: number;
  timeFrame: string;
  onFontSizeChange: (event: Event, value: number | number[]) => void;
  onTimeFrameChange: (event: SelectChangeEvent) => void;
}

const DashboardControls: React.FC<DashboardControlsProps> = ({
  fontSize,
  timeFrame,
  onFontSizeChange,
  onTimeFrameChange,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        bgcolor: 'grey.50',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        {/* Font Size Control */}
        <Box sx={{ minWidth: 250 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextIncreaseIcon color="action" fontSize="small" />
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
              Font Size
            </Typography>
            <Slider
              value={fontSize}
              onChange={onFontSizeChange}
              min={0.7}
              max={1.5}
              step={0.1}
              marks={[
                { value: 0.7, label: 'S' },
                { value: 1, label: 'M' },
                { value: 1.3, label: 'L' },
                { value: 1.5, label: 'XL' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
              sx={{
                width: 120,
                '& .MuiSlider-markLabel': {
                  fontSize: '0.7rem',
                },
              }}
            />
          </Stack>
        </Box>

        {/* Time Frame Control */}
        <Box sx={{ minWidth: 200 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <DateRangeIcon color="action" fontSize="small" />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="time-frame-label">Time Frame</InputLabel>
              <Select
                labelId="time-frame-label"
                value={timeFrame}
                label="Time Frame"
                onChange={onTimeFrameChange}
              >
                {TIME_FRAME_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Current Time Frame Display */}
        <Box>
          <Chip
            label={TIME_FRAME_OPTIONS.find(o => o.value === timeFrame)?.label || 'All Time'}
            color="primary"
            variant="outlined"
            icon={<DateRangeIcon />}
            size="small"
          />
        </Box>
      </Stack>
    </Paper>
  );
};

/**
 * Dashboard Content Component
 */
interface DashboardContentProps {
  dashboard: any;
  widgetData: Record<string, WidgetDataResult>;
  isLoading: boolean;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  dashboard,
  widgetData,
  isLoading,
}) => {
  if (dashboard.widgets.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Widgets Yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This dashboard is empty. Edit it to add widgets.
        </Typography>
      </Box>
    );
  }

  return (
    <WidgetGrid layoutId={dashboard.layoutId}>
      {dashboard.widgets.map((widget: DashboardWidget, index: number) => (
        <WidgetRenderer
          key={widget.id}
          widget={widget}
          data={widgetData[widget.id]}
          index={index}
          isLoading={isLoading && !widgetData[widget.id]}
        />
      ))}
    </WidgetGrid>
  );
};

export default DashboardViewPage;
